import React from 'react';
import PropTypes from 'prop-types';
import Breadcrumbs from './Breadcrumbs';
import { generateComponentDocs, generateStoryDocs } from '../../utils/documentationUtils';

export default {
  title: 'Components/Breadcrumbs',
  component: Breadcrumbs,
  parameters: {
    layout: 'left',
    ...generateComponentDocs(
      // What problem does it solve?
      'Provides a breadcrumb trail to enhance navigation and show the user’s current location within the app.',
      // When & how to use it?
      'Use Breadcrumbs on pages where users need navigational context, such as multi-level workflows or detailed views.',
      // Current Usage
      `Currently implemented in the:
      \n- Routes Page
      \n- Sites Page
      \n- Tags Page
      \n- Contact Page
      \n- Notes Page
      \n- Visits Page
      \n- Seasons Page
      \n- Download Page
      \n- Items For Distribution Page etc.`,
      // Related Components
      '\n- **[Icon](http://localhost:6006/?path=/docs/components-icons-icon--docs)**: Icons used for visual enhancement to displays the page icon at the top left of the page.\n\n',
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    className: {
      control: { type: 'text' },
      description: 'CSS classes for the Breadcrumbs component container.',
      table: {
        type: { summary: 'string' },
        defaultValue: { summary: '' },
      },
    },
    links: {
      control: { type: 'object' },
      description: 'Array of breadcrumb links.',
      table: {
        type: { summary: 'array' },
        defaultValue: { summary: '[]' },
      },
    },
  },
};

const BreadcrumbsTemplate = ({ className = '', links, shouldIncludeDashboard = true }) => (
  <Breadcrumbs className={className} links={links} shouldIncludeDashboard={shouldIncludeDashboard} />
);

BreadcrumbsTemplate.propTypes = {
  className: PropTypes.string,
  links: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  shouldIncludeDashboard: PropTypes.bool,
};

const Template = BreadcrumbsTemplate.bind({});

export const ContactsBreadcrumbs = Template.bind({});
ContactsBreadcrumbs.args = {
  className: 'pb-12',
  links: [
    { href: '/sites', name: 'Sites' },
    { href: '/contacts', name: 'Contacts' },
  ],
};
ContactsBreadcrumbs.parameters = generateStoryDocs(
  // When is this variant used?
  'This variant is used when navigating to the Contacts page via the Sites page.',
  // Where is it currently used on the product?
  'Currently used on the Contacts page to show the breadcrumb path from the Sites page.',
);

export const SeasonsBreadcrumbs = Template.bind({});
SeasonsBreadcrumbs.args = {
  className: 'mt-8',
  links: [
    { href: '/admin', name: 'Admin' },
    { href: '/seasons', name: 'Seasons' },
  ],
};
SeasonsBreadcrumbs.parameters = generateStoryDocs(
  // When is this variant used?
  'This variant is used when navigating to the Seasons page via the Admin page.',
  // Where is it currently used on the product?
  'Currently used on the Seasons page to show the breadcrumb path from the Admin page.',
);

export const TagsBreadcrumbs = Template.bind({});
TagsBreadcrumbs.args = {
  className: 'pb-12',
  links: [
    { href: '/sites', name: 'Sites' },
    { href: '/tags', name: 'Tags' },
  ],
};
TagsBreadcrumbs.parameters = generateStoryDocs(
  // When is this variant used?
  'This variant is used when navigating to the Tags page.',
  // Where is it currently used on the product?
  'Currently used on the Tags page to show the breadcrumb path.',
);

export const SitesBreadcrumbs = Template.bind({});
SitesBreadcrumbs.args = {
  className: 'pb-12',
  links: [
    { href: '/sites', name: 'Sites' },
  ],
};

SitesBreadcrumbs.parameters = generateStoryDocs(
  // When is this variant used?
  'This variant is used when navigating to the Sites page.',
  // Where is it currently used on the product?
  'Currently used on the Sites page to show the breadcrumb path.',
);

export const NotesBreadcrumbs = Template.bind({});
NotesBreadcrumbs.args = {
  className: 'pb-12',
  links: [
    { href: '/notes', name: 'Notes' },
  ],
};
NotesBreadcrumbs.parameters = generateStoryDocs(
  // When is this variant used?
  'This variant is used when navigating to the Notes page.',
  // Where is it currently used on the product?
  'Currently used on the Notes page to show the breadcrumb path.',
);

export const VisitsBreadcrumbs = Template.bind({});
VisitsBreadcrumbs.args = {
  className: 'pb-12',
  links: [
    { href: '/visits', name: 'Visits' },
  ],
};
VisitsBreadcrumbs.parameters = generateStoryDocs(
  // When is this variant used?
  'This variant is used when navigating to the Visits page.',
  // Where is it currently used on the product?
  'Currently used on the Visits page to show the breadcrumb path.',
);

export const DownloadBreadcrumbs = Template.bind({});
DownloadBreadcrumbs.args = {
  className: 'pb-12',
  links: [
    { href: '/', name: 'Home' },
    { href: '/download', name: 'Download' },
  ],
  shouldIncludeDashboard: false,
};
VisitsBreadcrumbs.parameters = generateStoryDocs(
  // When is this variant used?
  'This variant is used when navigating to the Download page.',
  // Where is it currently used on the product?
  'Currently used on the Download page to show the breadcrumb path.',
);
